import { useState, useMemo } from "react"
import { Table, Column, Data, FieldType, Section, FieldValue, Async } from "../../../../../../features/ui"
import { TableName } from "../../../../../../tables"
import { define } from "../../../../../../utils/typeUtils"
import { useUnitTypeLoaders } from "../../../../../stock"
import { useProductLoaders } from "../../../../loaders/productLoaders"
import { ProductName } from "../../product-name/productName"
import { useFind } from "../../../../../../state/reducers/hooks"
import { INITIAL_SORT_STATE, SortState, nextSortState, sortRows, withSortHeaders } from "../../../../../../features/data-types"

type Props = {
    rows: Data[]
    readOnly?: boolean
    onAdd?: () => void
    onEditRow?: (productId: string) => void
    onRemoveRow?: (productId: string) => void
}

export const LinkedProductsTable = (props: Props) => {
    const { rows, readOnly, onAdd, onEditRow, onRemoveRow } = props

    const productLoaders = useProductLoaders()
    const unitTypeLoaders = useUnitTypeLoaders()
    const find = useFind()

    const [sortState, setSortState] = useState<SortState>(INITIAL_SORT_STATE)
    const onColumnSort = (name: string) => setSortState(prev => nextSortState(prev, name))

    const columns: Column[] = [
        {
            name: 'productId_1',
            type: FieldType.TABLE,
            table: TableName.PRODUCTS,
            label: 'Producto 1',
            sortValue: (value?: FieldValue) => (find(TableName.PRODUCTS, value as string) as { name?: string })?.name ?? '',
            render: (value?: FieldValue) => <Async
                loadFn={async () => {
                    const product = await productLoaders().loadProduct(define(value) as string)
                    return <ProductName product={product} />
                }}
                deps={[rows]}
            />
        },
        {
            name: 'productId_2',
            type: FieldType.TABLE,
            table: TableName.PRODUCTS,
            label: 'Producto 2',
            sortValue: (value?: FieldValue) => (find(TableName.PRODUCTS, value as string) as { name?: string })?.name ?? '',
            render: (value?: FieldValue) => <Async
                loadFn={async () => {
                    const product = await productLoaders().loadProduct(define(value) as string)
                    return <ProductName product={product} />
                }}
                deps={[rows]}
            />
        },
        {
            name: 'equivalenceData',
            type: FieldType.NUMBER,
            label: 'Equivalencia',
            sortable: false,
            render: (value?: FieldValue) => {
                const row = define(rows.find(row => row.key === value))
                const unitType_1 = unitTypeLoaders().findUnitType(define(row.unitTypeId_1) as string)
                const unitType_2 = unitTypeLoaders().findUnitType(define(row.unitTypeId_2) as string)
                return <>1 {unitType_1?.abbreviation} = {row.equivalence} {unitType_2?.abbreviation}</>
            }
        }
    ]

    const sortedRows = useMemo(() => {
        if (!sortState.column || !sortState.direction) return rows
        const col = columns.find(c => c.name === sortState.column)
        if (!col) return rows
        return sortRows(rows, col, sortState.direction)
    }, [rows, sortState])

    const sortableColumns = withSortHeaders(columns, sortState, onColumnSort)

    return (
        <Section
            id="linkedProducts"
            space={12}
        >
            <Table
                columns={sortableColumns}
                rows={sortedRows}
                rowId='key'
                readOnly={readOnly}
                createIcon="plus"
                small
                onCreate={onAdd}
                onEdit={onEditRow}
                onRemove={onRemoveRow}
            />
        </Section>
    )
}
